import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetUpYourBrand = () => {
  const [activeSection, setActiveSection] = useState("create-identity");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["create-identity", "ai-assistance", "edit-preview"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Your Brand – xPAGE Help Center"
        description="Learn how to set up your brand with xPAGE. Create a logo, choose a color palette, and let AI assist in crafting a professional online store identity."
        keywords="xPAGE, set up brand, brand identity, logo creation, AI branding, eCommerce store branding"
        canonical="https://help.xpage.ai/help/quick-launch-checklist/Set%20Up%20Your%20Brand"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Set Up Your Brand
          </h1>
          <p className="text-sm text-systemLabel">
            Your brand is the foundation of your online presence. With xPage,
            you can create a cohesive and conversion-focused brand identity in
            just a few clicks. Setting up your brand ensures that your store
            looks polished, professional, and trustworthy from the start.
          </p>
        </header>

        {/* Create Brand Identity Section */}
        <section id="create-identity" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create your brand identity
          </h2>
          <p className="text-sm text-systemLabel mt-2 mb-4">
            After registration, you'll be prompted to set up your brand. Start
            by entering your brand or landing page name, this will be visible
            across your landing page. Next, choose how you'd like to build your
            brand visuals:
          </p>

          <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 mb-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-deepPurple/10 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-upload text-deepPurple"></i>
                </div>
                <h3 className="text-lg font-semibold text-systemTitle">
                  Upload your own logo
                </h3>
              </div>
              <p className="text-sm text-systemLabel">
                If you already have a logo, upload it directly from your device.
              </p>
            </div>

            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-deepPurple/10 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-robot text-deepPurple"></i>
                </div>
                <h3 className="text-lg font-semibold text-systemTitle">
                  Use xPage AI (coming soon){" "}
                </h3>
              </div>
              <p className="text-sm text-systemLabel">
                Don't have a logo yet? No problem. xPage's AI-powered branding
                assistant can generate a complete visual identity for you,
                including a logo, color palette, and font combinations.
              </p>
            </div>
          </div>

          <p className="text-sm text-systemLabel">
            You'll also choose a color palette that reflects your brand's
            personality. These colors will automatically be applied to buttons,
            text highlights, and key visual elements across your store.
          </p>
        </section>

        {/* AI Assistance Section */}
        <section id="ai-assistance" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Let AI assist your setup
          </h2>
          <p className="text-sm text-systemLabel mt-2 mb-4">
            To save time and maximize consistency, you can provide a product
            link (such as from AliExpress, Amazon, or a custom product page).
            xPage's AI engine will analyze the product and automatically:
          </p>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mb-4">
            <div className="grid md:grid-cols-1 gap-2">
              <ul className="list-disc list-inside space-y-1">
                <li className="text-sm text-systemLabel">
                  Extract product titles, images, and specifications
                </li>
                <li className="text-sm text-systemLabel">
                  Generate a tailored product description
                </li>
                <li className="text-sm text-systemLabel">
                  Create a layout with optimized content blocks
                </li>
              </ul>
            </div>
          </div>

          <p className="text-sm text-systemLabel">
            This speeds up the setup process and ensures your brand looks
            professionally crafted right from the start.
          </p>
        </section>

        {/* Edit and Preview Section */}
        <section id="edit-preview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Edit and preview
          </h2>
          <p className="text-sm text-systemLabel mt-2 mb-4">
            Once your brand details are set, you can preview how your store will
            look with your branding applied. If needed, you can return to the
            branding section at any time to make changes or upload updated
            assets.
          </p>
          <p className="text-sm text-systemLabel">
            Your store's branding will carry through every page, ensuring a
            consistent experience for every visitor and helping you build
            long-term trust with your audience.
          </p>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#create-identity"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "create-identity"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Identity
              </a>
            </li>
            <li>
              <a
                href="#ai-assistance"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "ai-assistance"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                AI Assistance
              </a>
            </li>
            <li>
              <a
                href="#edit-preview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "edit-preview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Edit & Preview
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetUpYourBrand;
